// Utilities for handling application base path (served behind a prefix like /mk)
// Can be overridden with MK_BASE_PATH at build/serve time

export const BASENAME: string = (process.env.MK_BASE_PATH || '/mk').replace(/\/$/, '')
export const PUBLIC_PATH: string = BASENAME === '' ? '/' : `${BASENAME}/`

/**
 * Prefix a path with the application's base path.
 * - External URLs (http/https) are returned unchanged.
 * - Absolute app paths (starting with "/") are prefixed with BASENAME, but never double-prefixed.
 * - Paths starting with "/us/en" are returned unchanged (no /mk prefix added).
 * - Relative paths are prefixed with PUBLIC_PATH.
 */
export function withBase(path: string): string {
  if (!path) return PUBLIC_PATH
  const lower = path.toLowerCase()
  if (lower.startsWith('http://') || lower.startsWith('https://')) return path
  if (path.startsWith('/')) {
    // Don't add /mk prefix to /us/en paths
    if (path.startsWith('/us/en')) return path
    // Avoid double prefixing if the path already starts with BASENAME
    if (BASENAME && (path === BASENAME || path.startsWith(`${BASENAME}/`))) return path
    return `${BASENAME}${path}`
  }
  return `${PUBLIC_PATH}${path}`
}

/**
 * Strip the BASENAME from the start of a path, returning a router-relative path
 * (suitable for React Router with basename configured).
 */
export function stripBase(path: string): string {
  if (!path) return '/'
  const lower = path.toLowerCase()
  if (lower.startsWith('http://') || lower.startsWith('https://')) return path
  if (!BASENAME) return path.startsWith('/') ? path : `/${path}`
  if (path === BASENAME) return '/'
  if (path.startsWith(`${BASENAME}/`)) return path.slice(BASENAME.length)
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * Convert any URL or path to a router-relative path (no BASENAME prefix, leading slash ensured).
 */
export function toRouterPath(path: string): string {
  if (!path) return '/'
  const lower = path.toLowerCase()
  // If absolute URL, convert to pathname
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    try {
      const u = new URL(path)
      return stripBase(u.pathname + (u.search || ''))
    } catch {
      return '/'
    }
  }
  return stripBase(path)
}
